import React, { useMemo, useEffect, useRef, useState } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { FontLoader, TextGeometry, OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import useEditorStore from '../store/useEditorStore';
import { FONTS, MATERIAL_TYPES } from '../mock/mock';
import { sharedCameraQuat } from '../shared/cameraSync';

const h = React.createElement;

function useFont(url) {
  const [font, setFont] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const loader = new FontLoader();
    loader.load(url, (f) => { if (!cancelled) setFont(f); });
    return () => { cancelled = true; };
  }, [url]);
  return font;
}

// Create a clip (small extruded plate that bridges two pieces)
function makeClip(opts = {}) {
  const w = opts.width || 0.06;
  const h = opts.height || 0.04;
  const d = opts.depth || 0.025;
  const grp = new THREE.Group();
  const geo = new THREE.BoxGeometry(w, h, d);
  const mat = new THREE.MeshStandardMaterial({
    color: '#c8c8d0', roughness: 0.4, metalness: 0.85,
  });
  const body = new THREE.Mesh(geo, mat);
  grp.add(body);
  // Tiny tab on each end for clip-style
  const tabGeo = new THREE.BoxGeometry(w * 0.3, h * 1.4, d * 0.3);
  const tab1 = new THREE.Mesh(tabGeo, mat);
  tab1.position.x = w * 0.5;
  grp.add(tab1);
  const tab2 = new THREE.Mesh(tabGeo, mat);
  tab2.position.x = -w * 0.5;
  grp.add(tab2);
  return grp;
}

// Create a wall standoff (mounting tube + chrome head)
function makeStandoff(height, diameter) {
  const r = diameter / 2;
  const grp = new THREE.Group();
  // Main shaft (aluminum)
  const shaftGeo = new THREE.CylinderGeometry(r, r, height, 24);
  const shaftMat = new THREE.MeshStandardMaterial({
    color: '#d4d4dc', roughness: 0.25, metalness: 0.95,
  });
  const shaft = new THREE.Mesh(shaftGeo, shaftMat);
  shaft.rotation.x = Math.PI / 2;
  shaft.castShadow = true;
  grp.add(shaft);
  // Chrome cap (decorative)
  const capGeo = new THREE.CylinderGeometry(r * 1.15, r * 1.05, height * 0.18, 24);
  const capMat = new THREE.MeshStandardMaterial({
    color: '#f0f0f5', roughness: 0.1, metalness: 1.0,
  });
  const cap = new THREE.Mesh(capGeo, capMat);
  cap.rotation.x = Math.PI / 2;
  cap.position.z = height / 2 - height * 0.09;
  grp.add(cap);
  return grp;
}

// Build an EXACT letter geometry using the same shape data as the walls (no bevel, matching silhouette).
// CRITICAL for boîtier mode: front face MUST have the same outer outline as the hollow walls,
// otherwise the face overhangs the walls in 3D and looks unprofessional (and is wrong in production).
function buildExactLetterGeo(font, text, size, depth, opts = {}) {
  const shapes = font.generateShapes(text || ' ', size);
  const geo = new THREE.ExtrudeGeometry(shapes, {
    depth,
    bevelEnabled: false,
    curveSegments: opts.curveSegments || 14,
  });
  geo.computeBoundingBox();
  geo.translate(0, 0, -depth / 2);
  geo.computeBoundingBox();
  return geo;
}

function buildLetterGeo(font, text, size, depth, opts = {}) {
  const geo = new TextGeometry(text || ' ', {
    font, size, height: depth,
    curveSegments: opts.curveSegments || 10,
    bevelEnabled: opts.bevelEnabled !== false,
    bevelThickness: opts.bevelThickness ?? 0.003,
    bevelSize: opts.bevelSize ?? 0.003,
    bevelOffset: 0,
    bevelSegments: opts.bevelSegments ?? 2,
  });
  geo.computeBoundingBox();
  // Center extrusion along Z (TextGeometry extrudes from 0 to depth)
  geo.translate(0, 0, -depth / 2);
  geo.computeBoundingBox();
  return geo;
}

// Inset (shrink) a 2D polygon by `dist`, assuming CCW outer / CW holes (THREE.Shape convention).
// Uses per-vertex angle-bisector offset — robust enough for letter contours.
function insetPolygon(points, dist, isOuter = true) {
  const n = points.length;
  if (n < 3 || dist <= 0) return points.slice();
  const out = [];
  for (let i = 0; i < n; i++) {
    const prev = points[(i - 1 + n) % n];
    const cur = points[i];
    const next = points[(i + 1) % n];
    // Edge vectors
    let e1x = cur.x - prev.x, e1y = cur.y - prev.y;
    let e2x = next.x - cur.x, e2y = next.y - cur.y;
    const l1 = Math.hypot(e1x, e1y) || 1;
    const l2 = Math.hypot(e2x, e2y) || 1;
    e1x /= l1; e1y /= l1; e2x /= l2; e2y /= l2;
    // Inward normals: for CCW outer, inward = rotate edge 90° LEFT = (-ey, ex)
    // For CW holes, inward (toward shape interior) = opposite = (ey, -ex)
    const s = isOuter ? 1 : -1;
    const n1x = -e1y * s, n1y = e1x * s;
    const n2x = -e2y * s, n2y = e2x * s;
    // Bisector
    let bx = n1x + n2x, by = n1y + n2y;
    const bl = Math.hypot(bx, by);
    if (bl < 1e-6) { out.push(new THREE.Vector2(cur.x, cur.y)); continue; }
    bx /= bl; by /= bl;
    // Miter length correction: 1 / sin(theta/2)
    const dot = Math.max(-1, Math.min(1, n1x * bx + n1y * by));
    const miter = 1 / Math.max(0.2, dot); // clamp to avoid spikes
    out.push(new THREE.Vector2(cur.x + bx * dist * miter, cur.y + by * dist * miter));
  }
  return out;
}

// Build a hollow letter geometry (true boîtier shell with an interior cavity)
// Handles letters with INTERNAL counters (O, A, e, g, etc.) by offsetting the full
// multi-polygon region (outer + holes) together.
// Optionally adds an inner RIM (lip) at the front for clipping the face avant on top.
function buildHollowLetterGeo(font, text, size, depth, wallThickness, rim = null) {
  let Offset;
  try {
    // eslint-disable-next-line global-require
    Offset = require('polygon-offset');
  } catch (e) {
    Offset = null;
  }
  const shapes = font.generateShapes(text || ' ', size);
  // For rim: remember the inner cavity polygons (post-walls) of each shape
  const cavityPolysPerShape = [];
  shapes.forEach((shape) => {
    if (!Offset) { cavityPolysPerShape.push({ shape, cavities: [] }); return; }
    try {
      const extracted = shape.extractPoints(40);
      const closePath = (pts) => {
        const path = pts.map((p) => [p.x, p.y]);
        if (path.length > 2) {
          const a = path[0], b = path[path.length - 1];
          if (Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9) path.pop();
        }
        return path;
      };
      const outerPath = closePath(extracted.shape);
      if (outerPath.length < 4) { cavityPolysPerShape.push({ shape, cavities: [] }); return; }
      const holePaths = (extracted.holes || []).map(closePath).filter((p) => p.length >= 4);

      const off = new Offset();
      const cavityPolys = off.data([outerPath, ...holePaths]).padding(wallThickness);
      if (!cavityPolys || !cavityPolys.length) { cavityPolysPerShape.push({ shape, cavities: [] }); return; }

      // Robustness: for letters like 'e', 'g', 'a', 'o' with existing counters, the offset can
      // create fragments or misaligned cavities that produce visual artifacts. We accept ONLY
      // a single, cleanly-sized cavity per shape. If the letter has existing holes (counters),
      // we skip hollowing entirely to keep the rendering clean (physically the hollow is subtle).
      const hasCounters = holePaths.length > 0;
      if (hasCounters) {
        // Skip hollow for letters with natural counters (e, o, a, g, p, q, d, b, R, B, D, ...)
        cavityPolysPerShape.push({ shape, cavities: [] });
        return;
      }
      // Only accept letters where offset produces EXACTLY one cavity polygon
      if (cavityPolys.length !== 1) {
        cavityPolysPerShape.push({ shape, cavities: [] });
        return;
      }
      const polyArea = (poly) => {
        let a = 0;
        for (let i = 0; i < poly.length; i++) {
          const p = poly[i], q = poly[(i + 1) % poly.length];
          a += p[0] * q[1] - q[0] * p[1];
        }
        return Math.abs(a) / 2;
      };
      const outerArea = polyArea(outerPath);
      const cavityAreaTotal = polyArea(cavityPolys[0]);
      const ratio = cavityAreaTotal / Math.max(outerArea, 1e-9);
      // Accept only reasonable cavities (thick enough walls, not collapsed)
      if (ratio < 0.15 || ratio > 0.75) {
        cavityPolysPerShape.push({ shape, cavities: [] });
        return;
      }
      const goodCavities = cavityPolys;

      goodCavities.forEach((poly) => {
        if (!poly || poly.length < 4) return;
        const reversed = poly.slice().reverse();
        const hp = new THREE.Path();
        hp.moveTo(reversed[0][0], reversed[0][1]);
        for (let i = 1; i < reversed.length; i++) hp.lineTo(reversed[i][0], reversed[i][1]);
        hp.closePath();
        shape.holes.push(hp);
      });
      cavityPolysPerShape.push({ shape, cavities: goodCavities });
    } catch (e) {
      cavityPolysPerShape.push({ shape, cavities: [] });
    }
  });
  const wallsGeo = new THREE.ExtrudeGeometry(shapes, {
    depth,
    bevelEnabled: false,
    curveSegments: 12,
  });
  wallsGeo.computeBoundingBox();
  wallsGeo.translate(0, 0, -depth / 2);
  wallsGeo.computeBoundingBox();

  // Build inner rim (horizontal lip): a thin slab between cavity outer edge and a smaller offset inside.
  // The rim sits at the FRONT of the boîtier, on which the face avant clips.
  if (rim && rim.enabled && rim.width > 0 && Offset) {
    const rimShapes = [];
    cavityPolysPerShape.forEach(({ cavities }) => {
      cavities.forEach((cavity) => {
        try {
          const off = new Offset();
          const inset = off.data([cavity]).padding(rim.width + (rim.clearance || 0));
          if (!inset || !inset.length) return;
          const rimShape = new THREE.Shape();
          rimShape.moveTo(cavity[0][0], cavity[0][1]);
          for (let i = 1; i < cavity.length; i++) rimShape.lineTo(cavity[i][0], cavity[i][1]);
          rimShape.closePath();
          inset.forEach((hole) => {
            if (!hole || hole.length < 4) return;
            const rev = hole.slice().reverse();
            const hp = new THREE.Path();
            hp.moveTo(rev[0][0], rev[0][1]);
            for (let i = 1; i < rev.length; i++) hp.lineTo(rev[i][0], rev[i][1]);
            hp.closePath();
            rimShape.holes.push(hp);
          });
          rimShapes.push(rimShape);
        } catch { /* skip */ }
      });
    });
    if (rimShapes.length) {
      const rimDepthW = Math.max(0.002, rim.depth / 250);
      const rimGeo = new THREE.ExtrudeGeometry(rimShapes, { depth: rimDepthW, bevelEnabled: false, curveSegments: 10 });
      // Position rim at the FRONT of the boîtier (just before the face avant plane)
      rimGeo.translate(0, 0, depth / 2 - rimDepthW);
      try {
        // eslint-disable-next-line global-require
        const { mergeGeometries } = require('three/examples/jsm/utils/BufferGeometryUtils.js');
        const merged = mergeGeometries([wallsGeo.toNonIndexed(), rimGeo.toNonIndexed()]);
        wallsGeo.dispose();
        rimGeo.dispose();
        merged.computeBoundingBox();
        return merged;
      } catch (e) {
        rimGeo.dispose();
      }
    }
  }
  return wallsGeo;
}

// Create a text sprite (always faces camera)
function makeTextSprite(text, opts = {}) {
  const fontSize = opts.fontSize || 44;
  const padding = opts.padding || 18;
  const bg = opts.bg || 'rgba(19,19,24,0.92)';
  const fg = opts.fg || '#ffffff';
  const border = opts.border || '#ec1d8c';
  const tmpCanvas = document.createElement('canvas');
  const tmpCtx = tmpCanvas.getContext('2d');
  tmpCtx.font = `600 ${fontSize}px Inter, Arial, sans-serif`;
  const textWidth = Math.ceil(tmpCtx.measureText(text).width);
  const W = textWidth + padding * 2;
  const H = fontSize + padding * 1.4;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const ctx = c.getContext('2d');
  // Rounded rect bg
  const r = 12;
  ctx.fillStyle = bg;
  ctx.strokeStyle = border;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(W - r, 0);
  ctx.quadraticCurveTo(W, 0, W, r);
  ctx.lineTo(W, H - r);
  ctx.quadraticCurveTo(W, H, W - r, H);
  ctx.lineTo(r, H);
  ctx.quadraticCurveTo(0, H, 0, H - r);
  ctx.lineTo(0, r);
  ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = fg;
  ctx.font = `600 ${fontSize}px Inter, Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, W / 2, H / 2);
  const tex = new THREE.CanvasTexture(c);
  tex.anisotropy = 8;
  tex.minFilter = THREE.LinearFilter;
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false });
  const sprite = new THREE.Sprite(mat);
  // Scale: keep readable size in world units
  const worldHeight = opts.scale || 0.18;
  sprite.scale.set((W / H) * worldHeight, worldHeight, 1);
  sprite.renderOrder = 999;
  return sprite;
}

// Build dimension callout: a line with arrows + label sprite + optional witness lines
// witness1 / witness2 : real corner points of the model the dimension refers to
function makeDimensionCallout(p1, p2, label, color = '#ec1d8c', witness1 = null, witness2 = null) {
  const grp = new THREE.Group();
  // Main dimension line (dashed)
  const geom = new THREE.BufferGeometry().setFromPoints([p1, p2]);
  const lineMat = new THREE.LineDashedMaterial({ color, dashSize: 0.06, gapSize: 0.04, linewidth: 1 });
  const line = new THREE.Line(geom, lineMat);
  line.computeLineDistances();
  grp.add(line);
  // Solid line overlay for visibility on short distances (e.g. depth)
  const solid = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints([p1, p2]),
    new THREE.LineBasicMaterial({ color, linewidth: 2, transparent: true, opacity: 0.9 }),
  );
  grp.add(solid);
  // Witness (extension) lines from the real model corner to the dimension endpoints
  const witnessMat = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.55 });
  if (witness1) {
    grp.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([witness1, p1]), witnessMat));
  }
  if (witness2) {
    grp.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints([witness2, p2]), witnessMat));
  }
  // Endpoint markers (small spheres)
  const sphGeo = new THREE.SphereGeometry(0.018, 8, 8);
  const sphMat = new THREE.MeshBasicMaterial({ color });
  [p1, p2].forEach((p) => {
    const s = new THREE.Mesh(sphGeo, sphMat);
    s.position.copy(p);
    grp.add(s);
  });
  // Label sprite at midpoint
  const mid = p1.clone().add(p2).multiplyScalar(0.5);
  const sprite = makeTextSprite(label, { scale: 0.16, fontSize: 38, border: color });
  sprite.position.copy(mid);
  grp.add(sprite);
  return grp;
}

function materialFor(matId, color, opts = {}) {
  const def = MATERIAL_TYPES.find((m) => m.id === matId)?.visual || MATERIAL_TYPES[0].visual;
  const ledOn = !!opts.ledOn;
  const isFront = !!opts.isFront;

  if (matId === 'pmma') {
    return new THREE.MeshPhysicalMaterial({
      color, roughness: def.roughness, metalness: 0,
      transmission: def.transmission ?? 0.25, transparent: true, opacity: def.opacity ?? 0.96,
      clearcoat: def.clearcoat ?? 1.0, clearcoatRoughness: 0.05,
      emissive: new THREE.Color(color),
      emissiveIntensity: ledOn ? (isFront ? 2.8 : 0.6) : (def.emissiveBase ?? 0.1),
      ior: 1.49, thickness: 0.4,
      toneMapped: !ledOn,
    });
  }
  if (matId === 'wood') {
    return new THREE.MeshStandardMaterial({
      color, roughness: 0.85, metalness: 0,
      emissive: new THREE.Color(color),
      emissiveIntensity: ledOn && isFront ? 1.2 : 0,
      toneMapped: !(ledOn && isFront),
    });
  }
  if (matId === 'pvc') {
    return new THREE.MeshStandardMaterial({
      color, roughness: 0.7, metalness: 0.02,
      emissive: new THREE.Color(color),
      emissiveIntensity: ledOn && isFront ? 1.6 : 0,
      toneMapped: !(ledOn && isFront),
    });
  }
  // PETG default
  return new THREE.MeshStandardMaterial({
    color, roughness: 0.45, metalness: 0.05,
    emissive: new THREE.Color(color),
    emissiveIntensity: ledOn && isFront ? 2.0 : 0,
    toneMapped: !(ledOn && isFront),
  });
}

function makeSegmentMesh(font, seg, dims, structureType, ledOn, socleRim, standoffs) {
  const heightScale = dims.height / 250;
  const depth = Math.max(0.05, dims.depth / 250);
  const safeText = (seg.text || ' ').slice(0, 24);
  const grp = new THREE.Group();

  // In "Lettres boîtier" (B) mode, the back plate and walls are a single physical piece (SOCLE)
  // → force the back to share the walls material & color
  const isBox = structureType === 'B';
  const backMatId = isBox ? (seg.materials?.walls || 'petg') : (seg.materials?.back || 'petg');
  const backColor = isBox ? seg.colors.walls : seg.colors.back;

  // Back plate — EXACT same outline as walls (no bevel) for perfect production fit
  const backDepth = Math.max(0.012, 0.015);
  const backGeo = buildExactLetterGeo(font, safeText, heightScale, backDepth, { curveSegments: 14 });
  const backMat = materialFor(backMatId, backColor, { ledOn, isFront: false });
  const backMesh = new THREE.Mesh(backGeo, backMat);
  backMesh.name = 'back';
  // Type 'B' = boîtier : fond intégré à l'arrière interne des parois
  backMesh.position.z = structureType === 'B' ? (-depth / 2 + backDepth / 2) : (-depth / 2 - 0.018);
  backMesh.userData.baseZ = backMesh.position.z;
  backMesh.castShadow = true;
  backMesh.receiveShadow = true;
  grp.add(backMesh);

  // Walls — in "Lettres boîtier" (B) mode, build a TRUE HOLLOW shell with an inner cavity.
  // wallThickness slider (mm) controls the rim thickness.
  const wallThk = Math.max(0.003, (dims.wallThickness || 3) / 250); // world units
  const wallsGeo = isBox
    ? buildHollowLetterGeo(font, safeText, heightScale, depth, wallThk, socleRim)
    : buildLetterGeo(font, safeText, heightScale, depth, {
        bevelEnabled: true, bevelThickness: 0.003, bevelSize: 0.003, bevelSegments: 2, curveSegments: 12,
      });
  const wallsMat = materialFor(seg.materials?.walls || 'petg', seg.colors.walls, { ledOn, isFront: false });
  // For hollow shells, render both sides so the interior is visible
  if (isBox) wallsMat.side = THREE.DoubleSide;
  const wallsMesh = new THREE.Mesh(wallsGeo, wallsMat);
  wallsMesh.name = 'walls';
  wallsMesh.position.z = 0;
  wallsMesh.userData.baseZ = 0;
  wallsMesh.castShadow = true;
  wallsMesh.receiveShadow = true;
  grp.add(wallsMesh);

  // Front face (PMMA or other) — EXACT same outline as walls (no bevel, no scaling) for perfect fit.
  // Production critical: face must NOT overhang the walls.
  if (structureType !== 'C') {
    const acrDepth = Math.max(0.012, dims.acrylicThickness / 400);
    // For boîtier mode, the face avant has exactly the SAME outer silhouette as the walls.
    // For F (encastrée), reduce slightly so it fits inside the wall opening.
    const ringFactor = structureType === 'F' ? 0.96 : 1.0;
    const acrSize = heightScale * ringFactor;
    const frontGeo = buildExactLetterGeo(font, safeText, acrSize, acrDepth, { curveSegments: 14 });
    // Re-align with walls bbox center
    const wb = wallsGeo.boundingBox; const fb = frontGeo.boundingBox;
    if (wb && fb) {
      const dx = (wb.min.x + wb.max.x) / 2 - (fb.min.x + fb.max.x) / 2;
      const dy = (wb.min.y + wb.max.y) / 2 - (fb.min.y + fb.max.y) / 2;
      frontGeo.translate(dx, dy, 0);
    }
    const frontMat = materialFor(seg.materials?.front || 'pmma', seg.colors.front, { ledOn, isFront: true });
    const frontMesh = new THREE.Mesh(frontGeo, frontMat);
    frontMesh.name = 'front';
    const frontEdge = depth / 2;
    let z = 0;
    // L = clipsée par l'avant, posée DEVANT les parois (alignée tangentiellement sans débord)
    if (structureType === 'L') z = frontEdge + acrDepth / 2 + 0.0015;
    // B = lettres boîtier : face-couvercle posée SUR les parois (comme un couvercle de boîte) — évite le Z-fighting
    else if (structureType === 'B') z = frontEdge + acrDepth / 2 + 0.0015;
    // F = encastrée à mi-paroi
    else if (structureType === 'F') z = 0;
    // P = perforée, légèrement en retrait
    else if (structureType === 'P') z = frontEdge - acrDepth / 2 - 0.002;
    frontMesh.position.z = z;
    frontMesh.userData.baseZ = z;
    frontMesh.castShadow = true;
    grp.add(frontMesh);
  } else {
    const frontGeo = buildExactLetterGeo(font, safeText, heightScale, 0.02, { curveSegments: 14 });
    const wb = wallsGeo.boundingBox; const fb = frontGeo.boundingBox;
    if (wb && fb) {
      const dx = (wb.min.x + wb.max.x) / 2 - (fb.min.x + fb.max.x) / 2;
      const dy = (wb.min.y + wb.max.y) / 2 - (fb.min.y + fb.max.y) / 2;
      frontGeo.translate(dx, dy, 0);
    }
    const frontMat = materialFor(seg.materials?.front || 'petg', seg.colors.front, { ledOn, isFront: true });
    const frontMesh = new THREE.Mesh(frontGeo, frontMat);
    frontMesh.name = 'front';
    const z = depth / 2 + 0.005;
    frontMesh.position.z = z;
    frontMesh.userData.baseZ = z;
    frontMesh.castShadow = true;
    grp.add(frontMesh);
  }

  grp.userData.bbox = wallsGeo.boundingBox?.clone() || new THREE.Box3();

  // ===== Standoffs per segment (attached to socle base, min 2) =====
  // Position standoffs along the BOTTOM of the segment's bounding box, on the back face,
  // and drill fixation holes through the back plate at the same locations.
  if (standoffs?.enabled && grp.userData.bbox) {
    const bb = grp.userData.bbox;
    const sCount = Math.max(2, standoffs.count || 2); // minimum 2 per element
    const standoffLen = standoffs.height / 250;
    const radius = (standoffs.diameter / 2) / 250;
    const holeR = radius + 0.001; // slight clearance for the screw insert
    const standoffGeo = new THREE.CylinderGeometry(radius, radius, standoffLen, 22);
    const standoffMat = new THREE.MeshStandardMaterial({
      color: standoffs.material === 'aluminum' ? '#d4d4dc' : '#5a5a62',
      roughness: 0.32, metalness: 0.92,
    });
    // Place standoffs along bottom edge with margin from sides (avoid letter contours)
    const segWidth = bb.max.x - bb.min.x;
    const margin = Math.min(0.06, segWidth * 0.12); // 6cm or 12% — whichever smaller
    const startX = bb.min.x + margin;
    const endX = bb.max.x - margin;
    const yBase = bb.min.y + 0.05; // 12mm from absolute bottom of letter (slight inset)
    const standoffPositionsLocal = [];
    for (let i = 0; i < sCount; i++) {
      const t = sCount === 1 ? 0.5 : i / (sCount - 1);
      const x = startX + (endX - startX) * t;
      standoffPositionsLocal.push([x, yBase]);
      const c = new THREE.Mesh(standoffGeo, standoffMat.clone());
      c.rotation.x = Math.PI / 2;
      // Z: behind the back plate, sticking out further back
      c.position.set(x, yBase, -depth / 2 - 0.018 - standoffLen / 2);
      c.castShadow = true;
      c.receiveShadow = true;
      c.name = 'standoff';
      grp.add(c);
      // Visual fixation hole indicator on the back plate (small dark cylinder)
      const holeGeo = new THREE.CylinderGeometry(holeR, holeR, 0.005, 18);
      const holeMat = new THREE.MeshStandardMaterial({ color: '#0c0c10', roughness: 1.0, metalness: 0.0 });
      const hole = new THREE.Mesh(holeGeo, holeMat);
      hole.rotation.x = Math.PI / 2;
      hole.position.set(x, yBase, -depth / 2 + 0.002);
      hole.name = 'fixation_hole_indicator';
      grp.add(hole);

      // Screw head — visible metallic dot at the standoff back end (where the screw goes into the wall)
      const screwHeadGeo = new THREE.CylinderGeometry(radius * 1.6, radius * 1.4, 0.008, 16);
      const screwHeadMat = new THREE.MeshStandardMaterial({
        color: '#e8e8ee', roughness: 0.22, metalness: 0.98,
      });
      const screwHead = new THREE.Mesh(screwHeadGeo, screwHeadMat);
      screwHead.rotation.x = Math.PI / 2;
      screwHead.position.set(x, yBase, -depth / 2 - 0.018 - standoffLen - 0.004);
      screwHead.name = 'screw_head';
      screwHead.userData.baseX = x;
      screwHead.userData.baseY = yBase;
      grp.add(screwHead);

      // Dashed guide line from standoff to a "wall" marker further back (visible in exploded view)
      const guideMat = new THREE.LineDashedMaterial({
        color: '#ec1d8c', dashSize: 0.02, gapSize: 0.018, linewidth: 1,
        transparent: true, opacity: 0.85,
      });
      guideMat.userData.isScrewGuide = true;
      const guideGeo = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(x, yBase, -depth / 2 - 0.018 - standoffLen),
        new THREE.Vector3(x, yBase, -depth / 2 - 0.018 - standoffLen - 0.6),
      ]);
      const guide = new THREE.Line(guideGeo, guideMat);
      guide.computeLineDistances();
      guide.name = 'screw_guide';
      guide.visible = false; // shown only in exploded view
      grp.add(guide);
    }
    // Save metadata for production export
    grp.userData.standoffPositions = standoffPositionsLocal.map(([x, y]) => ({
      x_mm: Math.round(x * 250 * 100) / 100,
      y_mm: Math.round(y * 250 * 100) / 100,
      diameter_mm: standoffs.diameter,
      height_mm: standoffs.height,
    }));
  }
  return grp;
}

function SignGroup() {
  const segments = useEditorStore((s) => s.segments);
  const fontIndex = useEditorStore((s) => s.fontIndex);
  const dims = useEditorStore((s) => s.dimensions);
  const structureType = useEditorStore((s) => s.structureType);
  const exploded = useEditorStore((s) => s.exploded);
  const explodeAmount = useEditorStore((s) => s.explodeAmount ?? 1.0);
  const partsVisibility = useEditorStore((s) => s.partsVisibility);
  const ledOn = useEditorStore((s) => s.ledOn);
  const socleRim = useEditorStore((s) => s.socleRim);
  const standoffs = useEditorStore((s) => s.standoffs);
  const showDimensions = useEditorStore((s) => s.showDimensions);
  const showAnnotations = useEditorStore((s) => s.showAnnotations);
  const planMode = useEditorStore((s) => s.planMode);
  const fontUrl = FONTS[fontIndex]?.url || FONTS[0].url;
  const font = useFont(fontUrl);

  const group = useMemo(() => new THREE.Group(), []);
  const annotationsGroup = useMemo(() => new THREE.Group(), []);
  const dimsGroup = useMemo(() => new THREE.Group(), []);

  // Serialize segments for dep comparison
  const segKey = useMemo(() =>
    segments.map((s) =>
      `${s.text}|${s.colors.walls}|${s.colors.back}|${s.colors.front}|${s.materials?.walls}|${s.materials?.back}|${s.materials?.front}`
    ).join('::'),
  [segments]);

  useEffect(() => {
    if (!font) return;
    while (group.children.length) {
      const c = group.children[0];
      group.remove(c);
      c.traverse?.((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
          else obj.material.dispose();
        }
      });
    }

    const built = segments.map((seg) => ({
      seg, grp: makeSegmentMesh(font, seg, dims, structureType, ledOn, socleRim, standoffs),
    }));

    const gap = 0.005;
    let cursorX = 0;
    const placed = [];
    const heightScale = dims.height / 250; // world units per mm scale (1 unit = 250 mm)
    built.forEach(({ seg, grp }, i) => {
      const bb = grp.userData.bbox;
      const w = (bb && isFinite(bb.max.x - bb.min.x)) ? (bb.max.x - bb.min.x) : 1;
      const minX = bb?.min.x ?? 0;
      grp.position.x = cursorX - minX;
      cursorX += w + gap;
      // Add extra gap if this OR next segment is detached (physical separation)
      const isLast = i === built.length - 1;
      if (!isLast) {
        const next = built[i + 1].seg;
        if (seg.detached || next.detached) {
          const extraMm = Math.max(seg.gapAfterMm || 0, next.gapAfterMm || 0, 30);
          cursorX += extraMm / 250; // mm → world units
        }
      }
      placed.push({ seg, grp, w });
    });
    const totalW = cursorX - gap;
    placed.forEach(({ grp }) => { grp.position.x -= totalW / 2; group.add(grp); });

    // Y-center
    const bbox = new THREE.Box3().setFromObject(group);
    if (isFinite(bbox.min.y) && isFinite(bbox.max.y)) {
      const yC = (bbox.min.y + bbox.max.y) / 2;
      placed.forEach(({ grp }) => { grp.position.y -= yC; });
    }

    // Re-attach annotations and dims groups (they were removed by the cleanup above)
    if (!annotationsGroup.parent) group.add(annotationsGroup);
    if (!dimsGroup.parent) group.add(dimsGroup);

    // ========== DIMENSION CALLOUTS around the FINAL centered model ==========
    while (dimsGroup.children.length) {
      const c = dimsGroup.children.pop();
      c.traverse?.((o) => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) { if (o.material.map) o.material.map.dispose(); o.material.dispose(); }
      });
    }
    // Compute bbox AFTER centering, excluding annotations/dims groups
    const bbAfter = new THREE.Box3();
    placed.forEach(({ grp: g }) => bbAfter.expandByObject(g));
    if (isFinite(bbAfter.min.x) && isFinite(bbAfter.max.x)) {
      const sx = bbAfter.max.x - bbAfter.min.x;
      const sy = bbAfter.max.y - bbAfter.min.y;
      const pad = Math.max(0.18, sy * 0.18);
      const widthMm = Math.round(dims.height * (sx / sy));
      // HAUTEUR (H) : verticale, à droite du modèle (axe Y)
      const hP1 = new THREE.Vector3(bbAfter.max.x + pad, bbAfter.min.y, bbAfter.max.z);
      const hP2 = new THREE.Vector3(bbAfter.max.x + pad, bbAfter.max.y, bbAfter.max.z);
      dimsGroup.add(makeDimensionCallout(hP1, hP2, `H : ${dims.height} mm`, '#ec1d8c'));
      // LARGEUR (L) : horizontale, en bas du modèle (axe X)
      const wP1 = new THREE.Vector3(bbAfter.min.x, bbAfter.min.y - pad, bbAfter.max.z);
      const wP2 = new THREE.Vector3(bbAfter.max.x, bbAfter.min.y - pad, bbAfter.max.z);
      dimsGroup.add(makeDimensionCallout(wP1, wP2, `L : ${widthMm} mm`, '#a855f7'));
      // PROFONDEUR (P) : axe Z, sur l'arête SUPÉRIEURE-GAUCHE, AU-DESSUS du modèle
      // De l'avant (z=max) vers l'arrière (z=min), bien dégagée au-dessus
      const dP1 = new THREE.Vector3(bbAfter.min.x, bbAfter.max.y + pad * 0.8, bbAfter.max.z);
      const dP2 = new THREE.Vector3(bbAfter.min.x, bbAfter.max.y + pad * 0.8, bbAfter.min.z);
      dimsGroup.add(makeDimensionCallout(dP1, dP2, `P : ${dims.depth} mm`, '#22d3ee'));
    }

    // ========== LED point lights inside each segment (when ON) ==========
    if (ledOn) {
      placed.forEach(({ seg, grp: segGrp }) => {
        const bb = segGrp.userData.bbox;
        if (!bb) return;
        const sw = (bb.max.x - bb.min.x);
        const sh = (bb.max.y - bb.min.y);
        // 2-3 lights along the width
        const nLights = Math.max(2, Math.ceil(sw / 0.5));
        for (let i = 0; i < nLights; i++) {
          const t = nLights === 1 ? 0.5 : i / (nLights - 1);
          const x = bb.min.x + sw * t;
          const y = bb.min.y + sh * 0.5;
          const pl = new THREE.PointLight(seg.colors.front, 1.4, 0.8, 2.0);
          pl.position.set(x, y, -0.02);
          pl.userData.isLed = true;
          segGrp.add(pl);
        }
      });
    }

    // ========== ANNOTATIONS per piece (material name on top) ==========
    while (annotationsGroup.children.length) {
      const c = annotationsGroup.children.pop();
      if (c.material?.map) c.material.map.dispose();
      if (c.material) c.material.dispose();
    }
    const matLabels = { pmma: 'PMMA', petg: 'PETG', pvc: 'PVC', wood: 'Bois' };
    const isBox = structureType === 'B';
    placed.forEach(({ seg, grp: segGrp }) => {
      segGrp.children.forEach((mesh) => {
        // In boîtier mode, walls + back form a single "Socle" piece
        const partLabel = mesh.name === 'front'
          ? 'Face avant'
          : isBox
            ? 'Socle'
            : (mesh.name === 'walls' ? 'Parois' : 'Fond');
        // Use walls material for both walls and back when in boîtier mode
        const effPartKey = isBox && mesh.name === 'back' ? 'walls' : mesh.name;
        const matId = seg.materials?.[effPartKey] || (mesh.name === 'front' ? 'pmma' : 'petg');
        const matName = matLabels[matId] || matId;
        const text = `${partLabel} \u2014 ${matName}`;
        const sprite = makeTextSprite(text, { scale: 0.14, fontSize: 36 });
        sprite.userData = { partName: mesh.name, segGrpIdx: placed.indexOf(placed.find((p) => p.seg.id === seg.id)) };
        annotationsGroup.add(sprite);
      });
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [font, segKey, dims.height, dims.depth, dims.acrylicThickness, dims.wallThickness, structureType, ledOn, socleRim, standoffs]);

  // Add annotation/dims groups to scene container
  useEffect(() => {
    group.add(annotationsGroup);
    group.add(dimsGroup);
    return () => {
      group.remove(annotationsGroup);
      group.remove(dimsGroup);
    };
  }, [group, annotationsGroup, dimsGroup]);

  useFrame(() => {
    const depth = Math.max(0.05, dims.depth / 250);
    const factor = (planMode || exploded) ? (explodeAmount || 1) : 0;
    // Swap screw guide colors in planMode (black on white background)
    group.traverse((obj) => {
      if (obj.material && obj.material.userData?.isScrewGuide) {
        obj.material.color.set(planMode ? '#111111' : '#ec1d8c');
      }
    });
    // Compute exact rest positions per piece based on current structure + dims
    const acrDepth = Math.max(0.012, dims.acrylicThickness / 400);
    const frontEdge = depth / 2;
    const restZ = {
      // 'B' = lettres boîtier : fond fusionné aux parois (boîtier creux ouvert)
      back: structureType === 'B' ? (-depth / 2 + 0.0075) : (-depth / 2 - 0.018),
      walls: 0,
      front: structureType === 'L' ? (frontEdge + acrDepth / 2 + 0.0015)
            : structureType === 'B' ? (frontEdge + acrDepth / 2 + 0.0015) // face clipsée SUR le boîtier
            : structureType === 'F' ? 0
            : structureType === 'P' ? (frontEdge - acrDepth / 2 - 0.002)
            : (depth / 2 + 0.005), // 'C'
    };
    group.children.forEach((segGrp) => {
      if (segGrp === annotationsGroup || segGrp === dimsGroup) return;
      segGrp.children.forEach((mesh) => {
        if (!mesh.isMesh) return;
        // Standoffs, holes, screw heads follow the back/socle in explosion
        if (mesh.name === 'standoff' || mesh.name === 'fixation_hole_indicator' || mesh.name === 'screw_head') {
          const followKey = structureType === 'B' ? 'walls' : 'back';
          mesh.visible = partsVisibility[followKey] !== false;
          return;
        }
        // Dashed screw guides — only visible when exploded
        if (mesh.name === 'screw_guide') {
          const followKey = structureType === 'B' ? 'walls' : 'back';
          mesh.visible = factor > 0.2 && partsVisibility[followKey] !== false;
          return;
        }
        if (!['back', 'walls', 'front'].includes(mesh.name)) return;
        const base = restZ[mesh.name] ?? 0;
        let targetZ = base;
        // In boîtier mode, back follows walls (single SOCLE piece — no separate explode for back)
        if (structureType === 'B' && mesh.name === 'back') {
          targetZ = base;
        } else if (mesh.name === 'back') {
          targetZ = base - depth * 2.5 * factor;
        } else if (mesh.name === 'front') {
          targetZ = base + depth * 2.5 * factor;
        }
        mesh.position.z += (targetZ - mesh.position.z) * 0.18;
        const visKey = (structureType === 'B' && mesh.name === 'back') ? 'walls' : mesh.name;
        mesh.visible = partsVisibility[visKey] !== false;
      });
    });

    // Position annotations above each piece
    let aIdx = 0;
    const segGroups = group.children.filter((c) => c !== annotationsGroup && c !== dimsGroup);
    segGroups.forEach((segGrp) => {
      segGrp.children.forEach((mesh) => {
        if (!mesh.isMesh || !['back', 'walls', 'front'].includes(mesh.name)) return;
        const sprite = annotationsGroup.children[aIdx++];
        if (!sprite) return;
        sprite.visible = !!showAnnotations && exploded && mesh.visible;
        if (!mesh.geometry || !mesh.geometry.boundingBox) {
          mesh.geometry?.computeBoundingBox?.();
        }
        const bb = mesh.geometry?.boundingBox;
        if (!bb) return;
        const worldPos = new THREE.Vector3((bb.min.x + bb.max.x) / 2, bb.max.y + 0.12, mesh.position.z);
        worldPos.add(segGrp.position);
        sprite.position.copy(worldPos);
      });
    });

    dimsGroup.visible = !!showDimensions;
  });

  const { camera } = useThree();
  useEffect(() => {
    if (!font) return;
    const t = setTimeout(() => {
      const bbox = new THREE.Box3().setFromObject(group);
      if (!isFinite(bbox.min.x) || !isFinite(bbox.max.x)) return;
      const size = new THREE.Vector3();
      bbox.getSize(size);
      const center = new THREE.Vector3();
      bbox.getCenter(center);
      const maxDim = Math.max(size.x, size.y * 1.6, size.z, 1);
      const dist = maxDim * 1.9;
      const cur = camera.position.clone().sub(center).normalize();
      camera.position.copy(center.clone().add(cur.multiplyScalar(dist)));
      camera.lookAt(center);
    }, 30);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [font, segments.map((s) => s.text).join('|'), dims.height, dims.depth, structureType]);

  return h('primitive', { object: group });
}

function Lighting() {
  const light = useEditorStore((s) => s.light);
  const dirRef = useRef();
  useFrame(() => {
    if (!dirRef.current) return;
    const rad = (light.horizontal * Math.PI) / 180;
    const r = 8;
    dirRef.current.position.set(Math.cos(rad) * r, (light.height / 168) * 6, Math.sin(rad) * r);
    dirRef.current.intensity = light.intensity * 2.2;
  });
  return h(React.Fragment, null,
    h('ambientLight', { intensity: 0.85 }),
    h('directionalLight', { ref: dirRef, position: [6, 6, 6], intensity: 2.6, castShadow: true }),
    h('directionalLight', { position: [-6, 4, -2], intensity: 1.2, color: '#ffe5f0' }),
    h('pointLight', { position: [-4, 3, 6], intensity: 1.4, color: '#ffcce0' }),
    h('pointLight', { position: [4, -2, 6], intensity: 1.0, color: '#aae0ff' }),
    h('pointLight', { position: [0, 4, 8], intensity: 1.3, color: '#ffffff' }),
    h('pointLight', { position: [0, -3, 4], intensity: 0.6, color: '#ffffff' }),
    h('hemisphereLight', { args: ['#bba0c8', '#3a2a45', 0.65] }),
  );
}

function GroundGrid() {
  const grid = useMemo(() => {
    const g = new THREE.GridHelper(40, 80, 0x353540, 0x26262e);
    g.position.y = -1.4;
    g.material.transparent = true;
    g.material.opacity = 0.45;
    return g;
  }, []);
  return h('primitive', { object: grid });
}

// Generate a wall texture (brick / concrete / wood / color) via canvas2D
function makeWallTexture(type, color) {
  const c = document.createElement('canvas');
  c.width = 1024; c.height = 1024;
  const ctx = c.getContext('2d');
  ctx.fillStyle = color || '#3a3a42';
  ctx.fillRect(0, 0, 1024, 1024);

  if (type === 'brick') {
    const bw = 128, bh = 56, gap = 4;
    ctx.fillStyle = '#5a2418';
    for (let row = 0; row < 1024 / bh + 1; row++) {
      const yy = row * bh;
      const off = (row % 2) ? bw / 2 : 0;
      for (let col = -1; col < 1024 / bw + 1; col++) {
        const xx = col * bw + off;
        // brick body with variation
        const grad = ctx.createLinearGradient(xx, yy, xx + bw, yy + bh);
        const vr = Math.floor(Math.random() * 30) - 15;
        grad.addColorStop(0, `rgb(${130 + vr}, ${60 + vr}, ${45 + vr})`);
        grad.addColorStop(1, `rgb(${90 + vr}, ${40 + vr}, ${30 + vr})`);
        ctx.fillStyle = grad;
        ctx.fillRect(xx + gap / 2, yy + gap / 2, bw - gap, bh - gap);
      }
    }
    // Mortar lines
    ctx.fillStyle = 'rgba(50,40,35,0.5)';
  } else if (type === 'concrete') {
    // Speckle noise
    const img = ctx.getImageData(0, 0, 1024, 1024);
    for (let i = 0; i < img.data.length; i += 4) {
      const n = (Math.random() - 0.5) * 50;
      img.data[i] = Math.max(0, Math.min(255, img.data[i] + n));
      img.data[i + 1] = Math.max(0, Math.min(255, img.data[i + 1] + n));
      img.data[i + 2] = Math.max(0, Math.min(255, img.data[i + 2] + n));
    }
    ctx.putImageData(img, 0, 0);
  } else if (type === 'wood') {
    // Vertical wood planks
    const pw = 110;
    for (let p = 0; p < 1024 / pw + 1; p++) {
      const x = p * pw;
      const grad = ctx.createLinearGradient(x, 0, x + pw, 0);
      const vr = Math.floor(Math.random() * 25);
      grad.addColorStop(0, `rgb(${110 + vr}, ${72 + vr}, ${40 + vr})`);
      grad.addColorStop(0.5, `rgb(${145 + vr}, ${95 + vr}, ${55 + vr})`);
      grad.addColorStop(1, `rgb(${110 + vr}, ${72 + vr}, ${40 + vr})`);
      ctx.fillStyle = grad;
      ctx.fillRect(x, 0, pw - 3, 1024);
      // Plank border
      ctx.fillStyle = 'rgba(40,25,15,0.7)';
      ctx.fillRect(x + pw - 3, 0, 3, 1024);
      // Grain lines
      for (let g = 0; g < 8; g++) {
        ctx.strokeStyle = `rgba(80, 50, 30, ${0.1 + Math.random() * 0.2})`;
        ctx.beginPath();
        ctx.moveTo(x + Math.random() * pw, 0);
        ctx.bezierCurveTo(
          x + Math.random() * pw, 256,
          x + Math.random() * pw, 768,
          x + Math.random() * pw, 1024
        );
        ctx.stroke();
      }
    }
  } else if (type === 'green') {
    // Vegetal wall - random green dots
    ctx.fillStyle = '#1a3a1a';
    ctx.fillRect(0, 0, 1024, 1024);
    for (let i = 0; i < 8000; i++) {
      const x = Math.random() * 1024;
      const y = Math.random() * 1024;
      const r = 4 + Math.random() * 14;
      const g = 80 + Math.floor(Math.random() * 110);
      ctx.fillStyle = `rgba(${20 + Math.random() * 30}, ${g}, ${30 + Math.random() * 30}, ${0.6 + Math.random() * 0.4})`;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  const tex = new THREE.CanvasTexture(c);
  tex.anisotropy = 8;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  return tex;
}

function BackgroundWall() {
  const background = useEditorStore((s) => s.background);
  const standoffs = useEditorStore((s) => s.standoffs);
  const dims = useEditorStore((s) => s.dimensions);
  const planMode = useEditorStore((s) => s.planMode);
  const mesh = useMemo(() => {
    const geo = new THREE.PlaneGeometry(40, 22);
    const mat = new THREE.MeshStandardMaterial({ color: '#888888', roughness: 0.85 });
    const m = new THREE.Mesh(geo, mat);
    m.receiveShadow = true;
    return m;
  }, []);

  // Position wall just behind the sign (accounting for standoff height if enabled)
  useFrame(() => {
    const depth = Math.max(0.05, dims.depth / 250);
    const standoffOffset = standoffs?.enabled ? (standoffs.height / 250) : 0;
    // Wall sits right behind the back plate (which is at -depth/2 - 0.018)
    mesh.position.z = -depth / 2 - 0.020 - standoffOffset;
  });

  useEffect(() => {
    if (background.type === 'none' || planMode) {
      mesh.visible = false;
      return;
    }
    mesh.visible = true;
    const oldMap = mesh.material.map;
    // Compute repeat count from tile size (mm) vs plane size in world units (plane = 40 units × 22 units = 10000 × 5500 mm)
    const tileMm = Math.max(20, background.textureScaleMm || 300);
    // Plane covers 40 world units on X = 40 × 250 mm = 10000 mm. Number of tiles = 10000 / tileMm
    const repeatX = Math.max(0.5, 10000 / tileMm);
    const repeatY = Math.max(0.5, 5500 / tileMm);
    if (background.type === 'color') {
      mesh.material.map = null;
      mesh.material.color.set(background.color);
    } else if (background.type === 'green') {
      const loader = new THREE.TextureLoader();
      loader.setCrossOrigin('anonymous');
      loader.load(
        'https://images.unsplash.com/photo-1585506774639-5e2f8fda6352?w=1024&q=80',
        (tex) => {
          tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
          tex.repeat.set(repeatX, repeatY);
          tex.anisotropy = 8;
          mesh.material.map = tex;
          mesh.material.color.set('#ffffff');
          mesh.material.needsUpdate = true;
        },
      );
    } else {
      const tex = makeWallTexture(background.type, background.color);
      tex.repeat.set(repeatX, repeatY);
      mesh.material.map = tex;
      mesh.material.color.set('#ffffff');
    }
    mesh.material.needsUpdate = true;
    if (oldMap) oldMap.dispose();
  }, [background.type, background.color, background.textureScaleMm, planMode, mesh]);

  return h('primitive', { object: mesh });
}

// Wall standoffs are now built PER SEGMENT inside makeSegmentMesh (attached to socle base).
// This stub remains to keep the existing scene tree intact during HMR.
function Standoffs() {
  return null;
}

function SceneSetup() {
  const { scene } = useThree();
  const planMode = useEditorStore((s) => s.planMode);
  useEffect(() => {
    if (planMode) {
      scene.background = new THREE.Color('#ffffff');
      scene.fog = new THREE.Fog('#ffffff', 20, 60);
    } else {
      scene.background = new THREE.Color('#050507');
      scene.fog = new THREE.Fog('#050507', 14, 38);
    }
  }, [scene, planMode]);
  return null;
}

function Controls() {
  const { camera, gl } = useThree();
  const ref = useRef();
  const orientation = useEditorStore((s) => s.orientation);

  useEffect(() => {
    const c = new OrbitControlsImpl(camera, gl.domElement);
    c.enableDamping = true;
    c.dampingFactor = 0.08;
    c.enablePan = true;
    c.screenSpacePanning = true; // pan in screen coords (feels natural on 2D interaction)
    c.panSpeed = 1.0;
    // Mouse mapping: left = rotate, middle = dolly, right = pan
    c.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
    // Touch mapping: 1 finger rotate, 2 fingers dolly + pan
    c.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    c.target.set(0, 0, 0);
    ref.current = c;
    // React to pan-mode toggle (from Zustand store)
    const unsub = useEditorStore.subscribe((s) => s.panMode, (panMode) => {
      if (!ref.current) return;
      if (panMode) {
        ref.current.mouseButtons = { LEFT: THREE.MOUSE.PAN, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.ROTATE };
        ref.current.touches = { ONE: THREE.TOUCH.PAN, TWO: THREE.TOUCH.DOLLY_ROTATE };
      } else {
        ref.current.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
        ref.current.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
      }
    });
    return () => { unsub?.(); c.dispose(); };
  }, [camera, gl]);

  useFrame(() => {
    if (ref.current) ref.current.update();
    sharedCameraQuat.copy(camera.quaternion);
  });

  useEffect(() => {
    const map = {
      front: [0, 0, 8], back: [0, 0, -8],
      top: [0, 8, 0.001], bottom: [0, -8, 0.001],
      left: [-8, 0, 0], right: [8, 0, 0],
      iso: [6, 5, 6], free: [4, 3, 7],
    };
    const p = map[orientation] || map.free;
    camera.position.set(p[0], p[1], p[2]);
    camera.lookAt(0, 0, 0);
    if (ref.current) { ref.current.target.set(0, 0, 0); ref.current.update(); }
  }, [orientation, camera]);

  return null;
}

function ZoomApplier() {
  const { camera, size } = useThree();
  const zoomRequest = useEditorStore((s) => s.zoomRequest);
  const clearZoomRequest = useEditorStore((s) => s.clearZoomRequest);
  useEffect(() => {
    if (zoomRequest == null) return;
    // CSS-px ≈ 3.78 per real mm on typical screen
    const cssPxPerMmScreen = 3.78;
    const targetPxPerMm = Math.max(0.0001, (zoomRequest / 100) * cssPxPerMmScreen);
    const fov = (camera.fov * Math.PI) / 180;
    const halfH = Math.tan(fov / 2);
    // pxPerMm = canvasH / (2 * d * halfH * 250)  → d
    const d = size.height / (2 * halfH * 250 * targetPxPerMm);
    const dir = camera.position.clone();
    if (dir.lengthSq() < 1e-6) dir.set(4, 3, 7);
    dir.normalize().multiplyScalar(Math.max(0.3, d));
    camera.position.copy(dir);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
    clearZoomRequest();
  }, [zoomRequest, size.width, size.height, camera, clearZoomRequest]);
  return null;
}

function ScaleProbe() {
  const setViewScale = useEditorStore((s) => s.setViewScale);
  const { camera, size } = useThree();
  const lastRef = useRef(0);
  useFrame(() => {
    // Throttle ~6 fps to avoid render churn
    const now = performance.now();
    if (now - lastRef.current < 160) return;
    lastRef.current = now;
    // Project two points 1 world-unit apart (= 250 mm) onto NDC, then to pixels
    const a = new THREE.Vector3(0, 0, 0).project(camera);
    const b = new THREE.Vector3(1, 0, 0).project(camera);
    const ndcDx = Math.abs(b.x - a.x);
    const pxFor250mm = (ndcDx * size.width) / 2;
    const pxPerMm = pxFor250mm / 250;
    if (!isFinite(pxPerMm) || pxPerMm <= 0) return;
    const pxPer100mm = pxPerMm * 100;
    // Update only when meaningful change to limit re-renders
    setViewScale({ pxPerMm, pxPer100mm });
  });
  return null;
}

function SceneContent() {
  const showGrid = useEditorStore((s) => s.showGrid);
  return h(React.Fragment, null,
    h(SceneSetup, null),
    h(Lighting, null),
    h(BackgroundWall, null),
    h(Standoffs, null),
    h(SignGroup, null),
    showGrid ? h(GroundGrid, null) : null,
    h(Controls, null),
    h(ScaleProbe, null),
    h(ZoomApplier, null),
  );
}

export default function Scene3D() {
  return h(Canvas, {
    shadows: true,
    camera: { position: [4, 3, 7], fov: 38 },
    gl: { antialias: true, alpha: true, preserveDrawingBuffer: true },
    style: { width: '100%', height: '100%' },
  }, h(SceneContent, null));
}
